import { useQuery } from '@tanstack/react-query';
import { adminSupabase } from '@/integrations/supabase/adminClient';
import { useAdminAuth } from '@/contexts/AdminAuthContext';

export type AdminScope =
  | 'super_admin'
  | 'platform_admin'
  | 'forkhire_admin'
  | 'forkseek_admin'
  | 'finance_admin'
  | 'support_admin'
  | null;

/**
 * Canonical scope → module visibility matrix.
 */
const SCOPE_VISIBILITY: Record<string, string[]> = {
  super_admin: [
    'dashboard', 'services', 'plans', 'plan-services', 'organizations',
    'ai-usage', 'ai-prompts', 'cv-design-library', 'consumption-overview', 'identity-requests',
    'email-templates',
    'bundles', 'add-ons', 'enterprise-deals', 'feature-flags',
    'system-issues', 'shadow-access', 'credits', 'subscriptions',
    'alerts', 'action-log', 'candidates', 'ai-failures',
    'payment-simulator', 'invoices', 'finance-tools',
    'governance-ops', 'governance-health', 'governance-inspect',
    'error-events', 'test-issues', 'payment-recovery', 'refunds',
    'stale-pending', 'plan-service-grid', 'banners', 'trials', 'intro-pricing',
  ],
  platform_admin: [
    'dashboard', 'services', 'organizations', 'ai-usage', 'ai-prompts', 'cv-design-library',
    'email-templates',
    'consumption-overview', 'system-issues', 'feature-flags',
    'alerts', 'action-log', 'candidates', 'ai-failures',
    'invoices', 'enterprise-deals', 'finance-tools',
    'governance-ops', 'governance-health', 'governance-inspect',
    'error-events', 'test-issues', 'payment-recovery', 'refunds',
    'stale-pending', 'banners',
  ],
  forkhire_admin: [
    'dashboard', 'organizations', 'ai-usage', 'identity-requests',
    'system-issues',
  ],
  forkseek_admin: [
    'dashboard', 'organizations', 'ai-usage', 'identity-requests',
    'system-issues', 'candidates',
  ],
  finance_admin: [
    'dashboard', 'plans', 'plan-services', 'bundles', 'add-ons',
    'enterprise-deals', 'credits', 'subscriptions', 'consumption-overview',
    'organizations', 'invoices', 'finance-tools', 'payment-recovery',
    'refunds', 'stale-pending',
  ],
  support_admin: [
    'dashboard', 'organizations', 'identity-requests',
    'shadow-access', 'system-issues', 'candidates', 'error-events', 'test-issues',
  ],
};

/**
 * Modules where platform_admin has read-only access (no mutations).
 */
const PLATFORM_ADMIN_READ_ONLY: string[] = [
  'finance-tools', 'invoices', 'enterprise-deals',
];

export function useAdminScope() {
  const { user } = useAdminAuth();

  const { data: scope = null, isLoading } = useQuery<AdminScope>({
    queryKey: ['admin-scope', user?.id],
    queryFn: async () => {
      const { data, error } = await adminSupabase.rpc('get_admin_scope', {
        _user_id: user!.id,
      });
      if (error || !data) return null;
      return data as AdminScope;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });

  const canAccess = (moduleKey: string): boolean => {
    if (!scope) return false;
    const allowed = SCOPE_VISIBILITY[scope];
    return allowed ? allowed.includes(moduleKey) : false;
  };

  /** True if the current scope can mutate data in this module (not read-only). */
  const canWrite = (moduleKey: string): boolean => {
    if (!scope) return false;
    if (!canAccess(moduleKey)) return false;
    if (scope === 'platform_admin' && PLATFORM_ADMIN_READ_ONLY.includes(moduleKey)) return false;
    return true;
  };

  return { scope, isLoading, canAccess, canWrite };
}
